import React from 'react';

import { Box, Typography, Divider } from '@mui/material';

import { BasicIcon } from '@/components/shared/icons/subscriptions/basic';

/**
 * Componente para mostrar un resumen de predicciones y estadísticas clave.
 * @param {Object} prediction - Objeto con datos de predicciones.
 */
const FixturePredictionSummary = ({ prediction }) => {
    if (!prediction) return null;

    const { teams } = prediction;

    /**
     * Agrupa las tarjetas en dos mitades.
     * @param {Object} cards - Datos de tarjetas (amarillas/rojas).
     * @returns {Object} - Totales y desgloses por mitades.
     */
    const groupCardsByHalf = (cards) => {
        if (!cards) return { firstHalf: 0, secondHalf: 0, details: {} };

        let firstHalf = 0;
        let secondHalf = 0;

        Object.entries(cards).forEach(([minute, data]) => {
            if (data.total) {
                const min = parseInt(minute.split('-')[0], 10);
                if (min <= 45) {
                    firstHalf += Number(data.total);
                } else {
                    secondHalf += Number(data.total);
                }
            }
        });

        return { firstHalf, secondHalf };
    };

    const homeYellowCards = groupCardsByHalf(teams?.home?.league?.cards?.yellow);
    const awayYellowCards = groupCardsByHalf(teams?.away?.league?.cards?.yellow);

    const homeRedCards = groupCardsByHalf(teams?.home?.league?.cards?.red);
    const awayRedCards = groupCardsByHalf(teams?.away?.league?.cards?.red);

    return (
        <Box sx={{ padding: 0, borderRadius: 2, marginTop: 4 }}>
            <Typography variant="h2" gutterBottom fontSize={16}>
                📊 Resumen del Partido en sobre las principales estadísticas
            </Typography>

            {/* ⚽ Goles */}
            <Box sx={{ marginBottom: 2 }}>
                <Box
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                    <BasicIcon width={16} />
                    <Typography variant="subtitle1" sx={{ fontSize: 14, mb: 1 }}>
                        Goles
                    </Typography>
                </Box>
                <Typography variant="body2" fontSize={14}>
                    El equipo {teams?.home?.name} ha marcado{' '}
                    {teams?.home?.league?.goals?.for?.total?.home || 'No disponible'} goles de local y{' '}
                    {teams?.home?.league?.goals?.for?.total?.away || 'No disponible'} de visitante, sumando un
                    total de {teams?.home?.league?.goals?.for?.total?.total || 'No disponible'} goles.
                    <br />
                    El equipo {teams?.away?.name} ha marcado{' '}
                    {teams?.away?.league?.goals?.for?.total?.home || 'No disponible'} goles de local y{' '}
                    {teams?.away?.league?.goals?.for?.total?.away || 'No disponible'} de visitante, para un
                    acumulado de {teams?.away?.league?.goals?.for?.total?.total || 'No disponible'} goles.
                </Typography>
            </Box>

            {/* 🟨 Tarjetas Amarillas */}
            <Box sx={{ marginBottom: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: 14, mb: 1 }}>
                    🟨 Tarjetas Amarillas
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    El equipo {teams?.home?.name} recibió un total de{' '}
                    {homeYellowCards.firstHalf + homeYellowCards.secondHalf} tarjetas amarillas:
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    - Primera mitad: {homeYellowCards.firstHalf} tarjetas. <br />- Segunda mitad:{' '}
                    {homeYellowCards.secondHalf} tarjetas.
                </Typography>

                <Typography variant="body2" sx={{ marginTop: 1 }} fontSize={14}>
                    El equipo {teams?.away?.name} recibió un total de{' '}
                    {awayYellowCards.firstHalf + awayYellowCards.secondHalf} tarjetas amarillas:
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    - Primera mitad: {awayYellowCards.firstHalf} tarjetas. <br />- Segunda mitad:{' '}
                    {awayYellowCards.secondHalf} tarjetas.
                </Typography>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: 14, mb: 1 }}>
                    🟥 Tarjetas Rojas
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    El equipo {teams?.home?.name} recibió un total de{' '}
                    {homeRedCards.firstHalf + homeRedCards.secondHalf} tarjetas rojas:
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    - Primera mitad: {homeRedCards.firstHalf} tarjetas. <br />- Segunda mitad:{' '}
                    {homeRedCards.secondHalf} tarjetas.
                </Typography>

                <Typography variant="body2" sx={{ marginTop: 1 }} fontSize={14}>
                    El equipo{teams?.away?.name} recibió un total de{' '}
                    {awayRedCards.firstHalf + awayRedCards.secondHalf} tarjetas rojas:
                </Typography>
                <Typography variant="body2" fontSize={14}>
                    - Primera mitad: {awayRedCards.firstHalf} tarjetas. <br />- Segunda mitad:{' '}
                    {awayRedCards.secondHalf} tarjetas.
                </Typography>
            </Box>
        </Box>
    );
};

export default FixturePredictionSummary;
